<template>
    <a-modal :id="modalId" class="s-trial-exit-banner" is-overlay-close>
        <div class="banner">
            <div class="content">
                <h2 class="title">
                    {{ content.title }}
                </h2>
                <p v-if="content.lead" class="lead">
                    {{ content.lead }}
                </p>
                <p v-if="content.description" class="description">
                    {{ content.description }}
                </p>
                <p
                    v-for="(link, index) in content.links"
                    :key="`link-${index}`"
                    class="link"
                >
                    <a-link
                        v-if="link.to"
                        glyph="arrow"
                        size="lead"
                        accent
                        v-bind="link"
                        @click="close"
                    />
                </p>
            </div>
            <div v-if="content.media" class="media">
                <a-picture
                    v-if="picture"
                    v-bind="picture"
                    fit="cover"
                />
                <div class="media-overlay" />
                <a-button
                    v-bind="button"
                    class="media-button"
                    :disable-clamp="true"
                    @click="onTour"
                />
            </div>
        </div>
        <a-demo-modal
            v-if="hasDemoModal"
            :modal="demoModal"
        />
    </a-modal>
</template>

<script setup>
import MicroModal from 'micromodal';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue';
import AButton from '@core/components/button/button.vue';
import ADemoModal from '@core/components/demo-modal/demo-modal.vue';
import ALink from '@core/components/link/link.vue';
import AModal from '@core/components/modal/modal.vue';
import APicture from '@core/components/picture/picture.vue';

const props = defineProps({
    content: { type: Object, required: true },
    enabled: { type: Boolean, default: true },
});

const modalId = `trial-exit-banner-${useId()}`;
const demoId = `${modalId}-demo`;
const isDemoMounted = ref(false);
const picture = computed(() => (props.content.media?.link
    ? { link: props.content.media.link, alt: props.content.media.alt || props.content.title }
    : null));
const button = computed(() => ({
    type: 'action',
    glyph: 'icon_play',
    layout: 'left-icon',
    ...props.content.media?.cta,
}));
const demoModal = computed(() => ({ ...button.value.modal, id: demoId }));
const hasDemoModal = computed(() => isDemoMounted.value && Boolean(demoModal.value.src));
const storageKey = 'trial-form-exit-intent-shown';
const hasExitBanner = computed(() => props.content.isAvailable === true && props.enabled);
let shown = false;
let isOpen = false;
let isDemoOpen = false;
let isTourClick = false;
let lastScrollY = 0;
let lastScrollTime = 0;
const SWIPE_UP_DISTANCE = 150;
const SWIPE_UP_TIME = 300;

function sendEvent(name) {
    const defaultEvents = {
        open: { eventAction: 'Shown', eventContent: props.content.title },
        close: { eventAction: 'Closed', eventContent: props.content.title },
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'Acronis',
        eventCategory: 'Popup',
        eventLabel: window.location.pathname,
        eventLocation: 'exit popup',
        ...defaultEvents[name],
        ...props.content.eventsGA?.[name],
    });
}

function disableExitBanner() {
    try {
        sessionStorage.setItem(storageKey, 'true');
    } catch {
        // Nothing to persist when browser storage is unavailable.
    }
}

function close() {
    if (isOpen) MicroModal.close(modalId);
}

function sendDemoEvent(name) {
    const event = demoModal.value.events?.[name];
    if (!event) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'Acronis', eventCategory: 'Interactions', ...event });
}

async function onTour() {
    isTourClick = true;
    close();
    isTourClick = false;
    if (!button.value.modal?.src) return;
    isDemoMounted.value = true;
    await nextTick();
    MicroModal.show(demoId, {
        openClass: 'is-modal-open',
        closeTrigger: 'data-close',
        disableScroll: true,
        onShow: () => {
            isDemoOpen = true;
            sendDemoEvent('open');
        },
        onClose: () => {
            if (!isDemoOpen) return;
            isDemoOpen = false;
            sendDemoEvent('close');
            isDemoMounted.value = false;
        },
    });
}

function show() {
    if (!hasExitBanner.value || shown) return;
    if (!props.content.title || (!button.value.to && !button.value.modal?.src)) return;
    if (document.querySelector('.is-modal-open')) return;

    try {
        if (sessionStorage.getItem(storageKey) === 'true') return;
    } catch {
        // Keep the in-memory limit when browser storage is unavailable.
    }

    MicroModal.show(modalId, {
        openClass: 'is-modal-open',
        closeTrigger: 'data-close',
        disableScroll: true,
        onShow: (modal) => {
            modal.querySelector('[role="dialog"]').setAttribute('aria-label', props.content.title);
            isOpen = true;
            shown = true;
            disableExitBanner();
            sendEvent('open');
        },
        onClose: () => {
            if (!isOpen) return;
            isOpen = false;
            if (!isTourClick) sendEvent('close');
        },
    });
}

function onMouseLeave(event) {
    if (event.clientY > 0) return;
    show();
}

// Touch devices have no mouseleave: a fast upward swipe is the closest exit-intent signal.
function onScroll() {
    const now = Date.now();
    const y = window.scrollY;
    const isFastUp = lastScrollY - y > SWIPE_UP_DISTANCE && now - lastScrollTime < SWIPE_UP_TIME;
    if (isFastUp) show();
    if (isFastUp || now - lastScrollTime >= SWIPE_UP_TIME) {
        lastScrollY = y;
        lastScrollTime = now;
    }
}

watch(hasExitBanner, (available) => {
    if (!available) close();
});

defineExpose({ disableExitBanner });

onMounted(() => {
    lastScrollY = window.scrollY;
    document.body.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('scroll', onScroll, { passive: true });
});
onBeforeUnmount(() => {
    document.body.removeEventListener('mouseleave', onMouseLeave);
    window.removeEventListener('scroll', onScroll);
    close();
    if (isDemoOpen) MicroModal.close(demoId);
    MicroModal.removeModal(demoId);
    MicroModal.removeModal(modalId);
});
</script>

<style lang="pcss" scoped>
:global(.a-modal.s-trial-exit-banner > .a-modal__wrapper > .a-modal__content) {
    width: 100%;
    max-width: 1002px;
}

.s-trial-exit-banner {
    .banner {
        gap: 32px;
        display: flex;
        padding: 24px;
        border-radius: 24px;
        flex-direction: column;
        color: var(--av-nav-primary);
        background: var(--av-inversed-primary);
        border: 1px solid var(--av-brand-secondary-light);
        animation: fade-in 2s 1 forwards;
        box-shadow: 0 10px 20px 0 rgba(36, 49, 67, 0.90);

        @media (--viewport-mobile-wide) {
            padding: 32px 40px;
        }

        @media (--viewport-desktop) {
            flex-direction: row;
        }
    }

    .content {
        @media (--viewport-desktop) {
            width: 388px;
            flex-shrink: 0;
        }
    }

    .title {
        @mixin display-strong;
        color: var(--av-nav-primary);

        @media (--viewport-mobile-wide) {
            @mixin hero-strong;
        }
    }

    .lead {
        @mixin lead;
        margin-top: 16px;
    }

    .description {
        @mixin lead;
        margin-top: 32px;
    }

    .link {
        margin-top: 8px;
    }

    .media {
        display: flex;
        min-width: 0;
        overflow: hidden;
        min-height: 296px;
        border-radius: 8px;
        position: relative;
        align-items: center;
        justify-content: center;
        background: var(--av-solid-brand-accent);

        @media (--viewport-desktop) {
            width: 100%;
        }

        > .a-picture,
        .media-overlay {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
        }

        :deep(.a-picture__img) {
            width: 100%;
            height: 100%;
        }

        .media-overlay {
            background: rgba(15, 29, 49, 0.65);
        }
    }

    .media-button {
        position: relative;
        max-width: calc(100% - 8px);

        &:focus-visible {
            --a-button-outline: none;
        }

        :deep(.a-button__content) {
            white-space: normal;
        }
    }
}

@keyframes fade-in {
    0% { opacity: 0; }
    100% { opacity: 1; }
}
</style>
